/**
 * Therme Singapore grandfathered public landing (default layout).
 * Optional experimental layout: ?layout=premium
 */
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Clock, MapPin, Sparkles } from 'lucide-react';
import {
  THERME_CEREMONY_LABEL,
  THERME_NOTICES,
  THERME_PROGRAMME,
  THERME_REGISTRATION_LABEL,
  THERME_VENUE_LABEL,
} from './therme-groundbreaking-content';
import { ThermeEventLogo, useThermePageTitle } from './therme-landing-shared';
import { ThermeGettingThereClassic } from './ThermeGettingThereSection';

const MONTHS_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function formatDateRange(startDate) {
  if (!startDate) return '';
  const d = new Date(`${startDate}T00:00:00`);
  const day = DAYS[d.getDay()];
  const date = d.getDate();
  const month = MONTHS_FULL[d.getMonth()];
  const year = d.getFullYear();
  return `${day} ${date} ${month} ${year}, 9:30 a.m.–11:30 a.m.`;
}

export default function ThermeGroundbreakingLandingClassic({ event: e, slug, registerPath }) {
  useThermePageTitle();

  const venueLabel = slug === 'thermesggbreak' ? THERME_VENUE_LABEL : e.venue;

  return (
    <div style={{ background: 'var(--bg-base)' }} className="min-h-screen text-[var(--text-primary)]">
      <header className="fixed top-0 inset-x-0 z-40 backdrop-blur-xl bg-[var(--bg-base-alpha70)] border-b border-[var(--border-default)]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <ThermeEventLogo event={e} />
          <div className="flex items-center gap-2 md:gap-3">
            <a href="#programme" className="btn-ghost text-sm hidden md:inline-flex">
              Programme
            </a>
            <a href="#getting-there" className="btn-ghost text-sm hidden md:inline-flex">
              Getting there
            </a>
            <Link to={registerPath} className="btn-primary" data-testid="public-register-cta">
              Register <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </header>

      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src={e.cover} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-base)] via-[var(--bg-base-alpha70)] to-transparent" />
        </div>
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 relative">
          <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.2em] text-[var(--text-secondary)] mb-6">
            <span className="w-1.5 h-1.5 bg-[var(--primary)] rounded-full pulse-dot" />
            {(e.startDate || 'TBD').replaceAll('-', '/')} — {(e.endDate || 'TBD').replaceAll('-', '/')}
          </div>
          <h1 className="font-heading text-[clamp(2.5rem,10vw,80px)] md:text-[150px] leading-[0.9] tracking-[-0.04em] font-semibold">
            {e.title}
            {e.startDate?.slice(0, 4) ? (
              <em className="not-italic block text-[var(--primary)]">/ {e.startDate.slice(0, 4)}</em>
            ) : null}
          </h1>
          <div className="mt-10">
            <Link to={registerPath} className="btn-primary" data-testid="hero-register">
              Save your seat <ArrowRight size={14} />
            </Link>
          </div>
          <div className="py-6 px-4 mt-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
            {[
              [Calendar, 'Date', formatDateRange(e.startDate)],
              [MapPin, 'Venue', venueLabel],
              [Clock, 'Registration', slug === 'thermesggbreak' ? THERME_REGISTRATION_LABEL : 'Opens on event day'],
              [Sparkles, 'Ceremony', slug === 'thermesggbreak' ? THERME_CEREMONY_LABEL : 'In-person'],
            ].map(([I, k, v]) => (
              <div key={k} className="bg-[var(--bg-surface)] p-5 flex flex-col gap-2">
                <I size={14} className="text-[var(--primary)]" />
                <div className="text-overline">{k}</div>
                <div
                  className={`font-mono text-sm mt-1 leading-relaxed break-words ${
                    k === 'Venue' ? 'whitespace-pre-line max-w-[64%] md:max-w-none' : ''
                  }`}
                >
                  {v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="programme" className="py-16 md:py-24 border-t border-[var(--border-default)]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="text-overline mb-4">On the day</div>
          <h2 className="font-heading text-2xl md:text-4xl tracking-tight font-medium leading-tight mb-10 md:mb-12">
            Programme highlights
          </h2>
          <div className="divide-y divide-[var(--border-default)] border-y border-[var(--border-default)]">
            {THERME_PROGRAMME.map((item) => (
              <article
                key={item.title}
                className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-6 py-6 md:py-8 items-start hover:bg-[var(--bg-surface)] -mx-2 px-2 transition-colors duration-200"
              >
                <div className="md:col-span-4">
                  <h3 className="font-heading text-lg md:text-xl leading-tight">{item.title}</h3>
                </div>
                <div className="md:col-span-8">
                  {item.speaker ? (
                    <p className="font-medium text-[var(--text-primary)]">{item.speaker}</p>
                  ) : null}
                  <p className="text-sm text-[var(--text-secondary)] mt-1 leading-relaxed whitespace-pre-line">{item.role}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-16 border-t border-[var(--border-default)]">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="text-overline mb-4">Good to know</div>
          <h2 className="font-heading text-2xl md:text-3xl tracking-tight font-medium leading-tight mb-8">
            Before you attend
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-[var(--border-default)] border border-[var(--border-default)]">
            {THERME_NOTICES.map(({ icon: Icon, label, value }) => (
              <div key={label} className="bg-[var(--bg-surface)] p-6 md:p-7 flex flex-col gap-3">
                <Icon size={16} className="text-[var(--primary)]" aria-hidden />
                <div className="text-overline">{label}</div>
                <p className="font-mono text-sm leading-relaxed">{value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <ThermeGettingThereClassic />

      <footer className="border-t border-[var(--border-default)] py-10">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 flex flex-wrap justify-between items-center gap-4">
          <ThermeEventLogo event={e} />
          <div className="text-[11px] font-mono text-[var(--text-tertiary)]">POWERED BY AIVORYEMS</div>
        </div>
      </footer>
    </div>
  );
}
